import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Upload, Download, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { api } from '@/lib/api.js';
import { formatHours } from '@/lib/format.js';
import { useToast } from '@/components/ui/Toast.jsx';

// ── Import format registry ────────────────────────────────────────────────────
const IMPORT_FORMATS = [
  {
    id: 'standard',
    label: 'Standard Format',
    description: 'One row per employee per day with in/out times and status.',
    sampleHeader: 'emp_no, date, in_time, out_time, status, hours_worked',
    type: 'row-per-day',
    requiredFields: ['emp_no', 'date'],
    optionalFields: ['in_time', 'out_time', 'status', 'hours_worked'],
    fieldLabels: {
      emp_no: 'Employee No',
      date: 'Date',
      in_time: 'In Time (optional)',
      out_time: 'Out Time (optional)',
      status: 'Status (optional)',
      hours_worked: 'Hours Worked (optional)',
    },
    fieldAliases: {
      emp_no: ['emp_no', 'employee_no', 'employee_id', 'empcode', 'emp_code', 'empno'],
      date: ['date', 'att_date', 'attendance_date'],
      in_time: ['in_time', 'time_in', 'in', 'clock_in'],
      out_time: ['out_time', 'time_out', 'out', 'clock_out'],
      status: ['status'],
      hours_worked: ['hours', 'hours_worked'],
    },
  },
  {
    id: 'punch',
    label: 'Punch / Time-Log Format',
    description: 'One row per clock punch — separate IN and OUT entries per employee per day.',
    sampleHeader: 'empno, date, time, status  (status = "in" or "out")',
    type: 'row-per-punch',
    requiredFields: ['emp_no', 'date', 'time', 'direction'],
    optionalFields: [],
    fieldLabels: {
      emp_no: 'Employee No',
      date: 'Date',
      time: 'Punch Time',
      direction: 'Direction (in / out)',
    },
    fieldAliases: {
      emp_no: ['emp_no', 'employee_no', 'empcode', 'emp_code', 'empno'],
      date: ['date', 'att_date', 'attendance_date'],
      time: ['time', 'punch_time', 'att_time', 'clock_time'],
      direction: ['status', 'direction', 'type', 'punch_type', 'inout', 'in_out'],
    },
    punchInValues: ['in', 'clock_in', 'entry', 'i', '1'],
    punchOutValues: ['out', 'clock_out', 'exit', 'o', '0', '2'],
  },
];

const FORMAT_MAP = Object.fromEntries(IMPORT_FORMATS.map((f) => [f.id, f]));

// ── Field definitions (standard — kept for template download) ─────────────────
const VALID_STATUSES = ['present', 'absent', 'half_day', 'leave', 'holiday', 'weekly_off'];
const STEPS = ['Choose Format', 'Upload', 'Map Columns', 'Preview & Validate', 'Commit'];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Convert Excel date serial or common date strings to YYYY-MM-DD */
function normalizeDate(v) {
  if (v === null || v === undefined || v === '') return '';
  // Excel serial number (number > 10000)
  const n = Number(v);
  if (!isNaN(n) && n > 10000) {
    const d = XLSX.SSF.parse_date_code(n);
    if (!d) return String(v);
    const mo = String(d.m).padStart(2, '0');
    const dy = String(d.d).padStart(2, '0');
    return `${d.y}-${mo}-${dy}`;
  }
  const s = String(v).trim();
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // DD/MM/YYYY
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  // MM/DD/YYYY — ambiguous but accept as fallback if day > 12
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy && Number(mdy[1]) <= 12 && Number(mdy[2]) > 12) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }
  // Try native Date parse as last resort
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
}

/** Normalize time values: HH:mm, H:mm, HH:mm:ss, or H:MM AM/PM, or Excel fraction */
function normalizeTime(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  // Excel time fraction 0–1
  if (!isNaN(n) && n >= 0 && n < 1) {
    const totalMins = Math.round(n * 24 * 60);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  // HH:mm or HH:mm:ss
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2]}`;
  // H:MM AM/PM
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = ampm[2];
    const period = ampm[3].toLowerCase();
    if (period === 'am' && h === 12) h = 0;
    if (period === 'pm' && h !== 12) h += 12;
    return `${String(h).padStart(2, '0')}:${m}`;
  }
  return s;
}

function calcHours(inT, outT) {
  if (!inT || !outT) return 0;
  const [hi, mi] = inT.split(':').map(Number);
  const [ho, mo] = outT.split(':').map(Number);
  const start = hi * 60 + (mi || 0);
  const end = ho * 60 + (mo || 0);
  if (end <= start) return 0;
  return Math.round(((end - start) / 60) * 100) / 100;
}

function validateRow(row, seenKeys) {
  const errs = [];
  if (!row.emp_no) errs.push('emp_no is required');
  if (!row.date) {
    errs.push('date is required');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date) || isNaN(Date.parse(row.date))) {
    errs.push(`date "${row.date}" is not a valid date`);
  }
  if (row.in_time && !/^\d{2}:\d{2}$/.test(row.in_time)) errs.push(`in_time "${row.in_time}" is invalid (use HH:mm)`);
  if (row.out_time && !/^\d{2}:\d{2}$/.test(row.out_time)) errs.push(`out_time "${row.out_time}" is invalid (use HH:mm)`);
  if (row.in_time && row.out_time && row.out_time <= row.in_time) errs.push('out_time must be after in_time');
  if (row.status && !VALID_STATUSES.includes(row.status)) errs.push(`status "${row.status}" must be one of: ${VALID_STATUSES.join(', ')}`);
  if (row.hours_worked !== '' && row.hours_worked !== undefined) {
    const n = Number(row.hours_worked);
    if (isNaN(n) || n < 0) errs.push('hours_worked must be a non-negative number');
  }
  // Duplicate within file
  if (row.emp_no && row.date && /^\d{4}-\d{2}-\d{2}$/.test(row.date)) {
    const key = `${row.emp_no}::${row.date}`;
    if (seenKeys.has(key)) errs.push(`Duplicate in file: ${row.emp_no} on ${row.date}`);
    else seenKeys.add(key);
  }
  return errs;
}

/** Convert punch rows (one per clock event) into one record per emp+day */
function pivotPunchRows(rawRows, mapping, format) {
  const groups = {};
  for (const raw of rawRows) {
    const empNo = String(raw[mapping.emp_no] ?? '').trim();
    const date  = normalizeDate(raw[mapping.date] ?? '');
    const time  = normalizeTime(raw[mapping.time] ?? '');
    const dir   = String(raw[mapping.direction] ?? '').trim().toLowerCase();
    if (!empNo || !date) continue;
    const key = `${empNo}::${date}`;
    if (!groups[key]) groups[key] = { emp_no: empNo, date, in_time: '', out_time: '', _warnings: [] };
    if (format.punchInValues.includes(dir)) {
      if (groups[key].in_time) groups[key]._warnings.push('Multiple IN punches — last kept');
      groups[key].in_time = time;
    } else if (format.punchOutValues.includes(dir)) {
      if (groups[key].out_time) groups[key]._warnings.push('Multiple OUT punches — last kept');
      groups[key].out_time = time;
    } else {
      groups[key]._warnings.push(`Unknown direction "${dir}" — ignored`);
    }
  }
  return Object.values(groups).map((r, idx) => {
    const hours = calcHours(r.in_time, r.out_time);
    return {
      _idx: idx + 1,
      emp_no: r.emp_no,
      date: r.date,
      in_time: r.in_time,
      out_time: r.out_time,
      status: r.in_time || r.out_time ? 'present' : 'absent',
      hours_worked: hours ? String(hours) : '',
      _warnings: r._warnings,
    };
  });
}

function downloadTemplate(formatId = 'standard') {
  if (formatId === 'punch') {
    const headers = ['empno', 'date', 'time', 'status'];
    const sample = [
      ['E001', '2026-05-01', '08:00', 'in'],
      ['E001', '2026-05-01', '17:00', 'out'],
      ['E002', '2026-05-01', '08:30', 'in'],
      ['E002', '2026-05-01', '17:30', 'out'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, 'attendance_punch_template.csv');
  } else {
    const headers = ['emp_no', 'date', 'in_time', 'out_time', 'status', 'hours_worked'];
    const sample = [
      ['E001', '2026-05-01', '08:00', '17:00', 'present', ''],
      ['E002', '2026-05-01', '08:30', '17:30', 'present', ''],
      ['E003', '2026-05-01', '', '', 'absent', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, 'attendance_import_template.csv');
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export function AttendanceImportDialog({ open, onOpenChange, onSuccess }) {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [formatId, setFormatId] = useState('standard');
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [mode, setMode] = useState('overwrite'); // 'overwrite' | 'skip'
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  function reset() {
    setStep(0); setFormatId('standard'); setRawRows([]); setHeaders([]); setMapping({});
    setPreview([]); setResult(null); setSubmitting(false); setMode('overwrite');
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleClose(v) {
    if (!v) reset();
    onOpenChange(v);
  }

  // Step 1: parse file
  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = new Uint8Array(ev.target.result);
      const wb = XLSX.read(data, { type: 'array', raw: false, cellDates: false });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (rows.length === 0) { toast({ variant: 'error', title: 'Empty file', description: 'No data rows found.' }); return; }
      const hdrs = Object.keys(rows[0]);
      setHeaders(hdrs);
      setRawRows(rows);
      // Auto-map based on selected format's aliases
      const fmt = FORMAT_MAP[formatId];
      const allFields = [...fmt.requiredFields, ...fmt.optionalFields];
      const autoMap = {};
      for (const field of allFields) {
        const aliases = fmt.fieldAliases[field] || [field];
        const match = hdrs.find((h) => aliases.includes(h.trim().toLowerCase()));
        if (match) autoMap[field] = match;
      }
      setMapping(autoMap);
      setStep(2);
    };
    reader.readAsArrayBuffer(file);
  }

  // Step 2 → 3: build preview
  function buildPreview() {
    const fmt = FORMAT_MAP[formatId];
    if (fmt.type === 'row-per-punch') {
      // Pivot punch rows first, then validate
      const pivoted = pivotPunchRows(rawRows, mapping, fmt);
      const seenKeys = new Set();
      const validated = pivoted.map((row) => ({
        ...row,
        _errors: validateRow(row, seenKeys),
      }));
      setPreview(validated);
    } else {
      // Standard: one row → one record
      const allFields = [...fmt.requiredFields, ...fmt.optionalFields];
      const seenKeys = new Set();
      const mapped = rawRows.map((raw, idx) => {
        const row = { _idx: idx + 1 };
        for (const field of allFields) {
          const hdr = mapping[field];
          let val = hdr ? String(raw[hdr] ?? '').trim() : '';
          if (field === 'date') val = normalizeDate(raw[hdr] ?? '');
          if (field === 'in_time' || field === 'out_time') val = normalizeTime(raw[hdr] ?? '');
          if (field === 'status') val = val.toLowerCase() || '';
          row[field] = val;
        }
        if (!row.hours_worked && row.in_time && row.out_time) {
          row.hours_worked = String(calcHours(row.in_time, row.out_time));
        }
        row._errors = validateRow(row, seenKeys);
        row._warnings = [];
        return row;
      });
      setPreview(mapped);
    }
    setStep(3);
  }

  // Step 3: dry-run placeholder → Step 4
  async function runDryRun() {
    const validRows = preview.filter((r) => r._errors.length === 0);
    if (validRows.length === 0) { setStep(4); return; }
    setStep(4);
  }

  // Step 4: commit
  async function handleSubmit() {
    const validRows = preview.filter((r) => r._errors.length === 0);
    if (validRows.length === 0) return;
    setSubmitting(true);
    try {
      const payload = validRows.map(({ emp_no, date, in_time, out_time, status, hours_worked }) => ({
        emp_no,
        date,
        in_time: in_time || null,
        out_time: out_time || null,
        status: status || 'present',
        hours_worked: Number(hours_worked || 0),
      }));
      const res = await api.post('/attendance/import-by-empno', { rows: payload, mode, dryRun: false });
      setResult(res);
      onSuccess?.(res);
    } catch (err) {
      if (err.code === 'PERIOD_LOCKED') {
        toast({ variant: 'error', title: 'Period locked', description: 'One or more months in your file are finalized.' });
      }
      setResult({ imported: 0, updated: 0, skipped: 0, errors: [{ row: '-', message: err.message }] });
    } finally {
      setSubmitting(false);
    }
  }

  const fmt = FORMAT_MAP[formatId];
  const allFields = [...fmt.requiredFields, ...fmt.optionalFields];
  const errorRows = preview.filter((r) => r._errors.length > 0);
  const validCount = preview.length - errorRows.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Import Attendance</DialogTitle>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-1 shrink-0">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                i < step ? 'bg-brand-500 text-white' : i === step ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-500 dark:bg-brand-900 dark:text-brand-300' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'
              }`}>{i + 1}</div>
              <span className={`text-xs ${i === step ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>{label}</span>
              {i < STEPS.length - 1 && <span className="text-slate-200 dark:text-slate-700 mx-1">›</span>}
            </div>
          ))}
        </div>

        {/* Step 0 — Choose Format */}
        {step === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">Select the format that matches your import file.</p>
            <div className="grid grid-cols-1 gap-3">
              {IMPORT_FORMATS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFormatId(f.id)}
                  className={`text-left rounded-lg border-2 p-4 transition-colors ${
                    formatId === f.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-brand-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                      formatId === f.id ? 'border-brand-500' : 'border-slate-300 dark:border-slate-600'
                    }`}>
                      {formatId === f.id && <div className="h-2 w-2 rounded-full bg-brand-500" />}
                    </div>
                    <div className="space-y-1 min-w-0">
                      <div className="font-medium text-sm text-slate-900 dark:text-slate-100">{f.label}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{f.description}</div>
                      <div className="font-mono text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 rounded px-2 py-1 mt-1">{f.sampleHeader}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={() => setStep(1)}>Next →</Button>
            </div>
          </div>
        )}

        {/* Step 1 — Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Upload a <strong>.csv</strong>, <strong>.txt</strong>, <strong>.xlsx</strong>, or <strong>.xls</strong> file.
              Format: <span className="font-medium text-brand-600 dark:text-brand-400">{FORMAT_MAP[formatId].label}</span>.
              First row must be a header row.
            </p>
            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 cursor-pointer hover:border-brand-400 transition-colors">
              <Upload className="h-8 w-8 text-slate-400" />
              <span className="text-sm text-slate-500">Click to browse or drag &amp; drop</span>
              <span className="text-xs text-slate-400">.csv, .txt, .xlsx, .xls</span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                className="sr-only"
                onChange={handleFile}
              />
            </label>
            <div className="flex justify-between items-center pt-1">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button variant="outline" size="sm" type="button" onClick={() => downloadTemplate(formatId)}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download Template
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Map Columns */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Match your file's columns to the required fields.{' '}
              <span className="font-medium">{rawRows.length} rows</span> detected.
              {fmt.type === 'row-per-punch' && (
                <span className="ml-1 text-brand-600 dark:text-brand-400">(Punch rows will be pivoted into daily records.)</span>
              )}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {allFields.map((field) => (
                <div key={field} className="space-y-1">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {fmt.fieldLabels[field]}
                    {fmt.requiredFields.includes(field) && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <Select
                    value={mapping[field] || ''}
                    onChange={(e) => setMapping({ ...mapping, [field]: e.target.value || undefined })}
                  >
                    <option value="">{fmt.optionalFields.includes(field) ? '— Not mapped —' : '— Select column —'}</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={buildPreview} disabled={fmt.requiredFields.some((f) => !mapping[f])}>
                Preview →
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Preview & Validate */}
        {step === 3 && !result && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium text-green-600 dark:text-green-400">{validCount} valid</span>
                {errorRows.length > 0 && (
                  <span className="ml-2 font-medium text-red-500">{errorRows.length} with errors (will be skipped)</span>
                )}
                {fmt.type === 'row-per-punch' && (
                  <span className="ml-2 text-slate-400">({rawRows.length} punch rows → {preview.length} daily records)</span>
                )}
              </p>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Existing records:</span>
                <Select value={mode} onChange={(e) => setMode(e.target.value)} className="h-7 text-xs py-0">
                  <option value="overwrite">Overwrite</option>
                  <option value="skip">Skip</option>
                </Select>
              </div>
            </div>

            <div className="overflow-auto max-h-72 rounded border border-slate-200 dark:border-slate-700">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-slate-500">#</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Emp No</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Date</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">In</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Out</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Status</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Hrs</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Validation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {preview.map((row) => {
                    const hasErr = row._errors.length > 0;
                    const hasWarn = row._warnings?.length > 0;
                    return (
                      <tr key={row._idx} className={hasErr ? 'bg-red-50 dark:bg-red-950/30' : hasWarn ? 'bg-amber-50 dark:bg-amber-950/20' : ''}>
                        <td className="px-2 py-1.5 text-slate-400">{row._idx}</td>
                        <td className="px-2 py-1.5 font-mono">{row.emp_no || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{row.date || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{row.in_time || '—'}</td>
                        <td className="px-2 py-1.5 font-mono">{row.out_time || '—'}</td>
                        <td className="px-2 py-1.5">{row.status || 'present'}</td>
                        <td className="px-2 py-1.5 font-mono tabular-nums">{row.hours_worked ? formatHours(Number(row.hours_worked)) : '—'}</td>
                        <td className="px-2 py-1.5">
                          {hasErr ? (
                            <span className="text-red-500 flex items-start gap-1">
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{row._errors[0]}{row._errors.length > 1 ? ` (+${row._errors.length - 1})` : ''}</span>
                            </span>
                          ) : hasWarn ? (
                            <span className="text-amber-600 dark:text-amber-400 flex items-start gap-1">
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{row._warnings[0]}{row._warnings.length > 1 ? ` (+${row._warnings.length - 1})` : ''}</span>
                            </span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" />OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={runDryRun} disabled={validCount === 0}>
                Commit {validCount} rows →
              </Button>
            </div>
          </div>
        )}

        {/* Step 4 — Confirm & Commit */}
        {step === 4 && !result && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Ready to import <span className="font-semibold text-green-600">{validCount} valid rows</span>
              {errorRows.length > 0 && <span className="ml-1 text-slate-500">({errorRows.length} skipped due to errors)</span>}.
              Existing records will be <span className="font-semibold">{mode === 'overwrite' ? 'overwritten' : 'skipped'}</span>.
            </p>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button onClick={handleSubmit} disabled={submitting || validCount === 0}>
                {submitting ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Importing…</> : 'Import'}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Import complete</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-green-50 dark:bg-green-950/30 p-3">
                <div className="text-2xl font-bold text-green-600">{result.imported ?? 0}</div>
                <div className="text-xs text-slate-500 mt-0.5">Inserted</div>
              </div>
              <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3">
                <div className="text-2xl font-bold text-blue-600">{result.updated ?? 0}</div>
                <div className="text-xs text-slate-500 mt-0.5">Updated</div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                <div className="text-2xl font-bold text-slate-500">{result.skipped ?? 0}</div>
                <div className="text-xs text-slate-500 mt-0.5">Skipped</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-auto rounded border border-red-200 dark:border-red-800 p-2">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">Errors ({result.errors.length})</p>
                {result.errors.map((e, i) => (
                  <div key={i} className="text-xs text-red-500">Row {e.row}: {e.message}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleClose(false)}>Close</Button>
          ) : step === 0 ? (
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
